/**
 * 🟦 V12.0 — LyonStylePacksPanel.
 *
 * Lista los Style Packs disponibles desde GET /api/style-packs.
 * Al aplicar uno: combina setTheme (uib-set-theme) + aplicación de las
 * recetas incluidas (applyRecipeToNode al root o nodo activo).
 *
 * Componente con state propio (fetch + apply). El aplicar real se delega
 * via callback `onApplyPack` que el padre conecta a UiBuilderPage.
 */
import { useEffect, useState, useCallback } from 'react';
import { X, Loader2, Sparkles, CheckCircle2, Tag, Layers } from 'lucide-react';
import { BACKEND_URL } from '../../config/api';
import LyonStylePackDiffViewer from './LyonStylePackDiffViewer';
import { isUserPremium, showPremiumGateMessage } from '../../genio/premiumGate';

const API = `${BACKEND_URL}/api`;

const LyonStylePacksPanel = ({ open, onClose, onApplyPack, currentTheme, es = true }) => {
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [applyState, setApplyState] = useState({}); // {id: 'loading'|'ok'|'err'}
  // 🟦 V12.1 — Diff Viewer state.
  const [diffPack, setDiffPack] = useState(null);
  const [applying, setApplying] = useState(false);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(`${API}/style-packs`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => {
        if (cancelled) return;
        setPacks(Array.isArray(d?.packs) ? d.packs : []);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.message || 'fetch_error');
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open]);
  /* eslint-enable react-hooks/set-state-in-effect */

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const handleOpenDiff = useCallback((pack) => {
    // 🟥 V18.0 — Premium Gate: aplicar un Style Pack MODIFICA el
    // proyecto del usuario. Bloqueamos antes de mostrar el diff.
    if (!isUserPremium()) {
      showPremiumGateMessage();
      return;
    }
    setDiffPack(pack);
  }, []);

  const handleConfirmApply = useCallback(async () => {
    if (!diffPack) return;
    if (!isUserPremium()) {
      showPremiumGateMessage();
      setDiffPack(null);
      return;
    }
    setApplyState((p) => ({ ...p, [diffPack.id]: 'loading' }));
    setApplying(true);
    try {
      const res = await onApplyPack(diffPack);
      setApplyState((p) => ({ ...p, [diffPack.id]: res?.ok ? 'ok' : 'err' }));
    } catch {
      setApplyState((p) => ({ ...p, [diffPack.id]: 'err' }));
    } finally {
      setApplying(false);
      setDiffPack(null);
    }
  }, [diffPack, onApplyPack]);

  if (!open) return null;
  return (
    <div
      data-testid="lyon-stylepacks-panel"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      className="fixed inset-0 z-[10000] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-slate-950 border border-purple-500/30 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <div className="px-5 py-3 border-b border-slate-800 flex items-center justify-between bg-gradient-to-r from-purple-950/50 to-fuchsia-950/40">
          <h3 className="text-sm font-bold text-white flex items-center gap-2">
            <Layers className="w-4 h-4 text-purple-300" />
            {es ? 'Style Packs IA' : 'AI Style Packs'}
            <span className="text-[10px] font-mono text-slate-400">({packs.length})</span>
          </h3>
          <button
            type="button"
            onClick={onClose}
            data-testid="lyon-close-stylepacks"
            className="p-1 rounded hover:bg-slate-800 text-slate-400 hover:text-white"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {loading && (
            <div className="flex items-center justify-center py-16 text-slate-400 text-sm">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              {es ? 'Cargando packs…' : 'Loading packs…'}
            </div>
          )}
          {error && !loading && (
            <div className="text-center py-10 text-red-300 text-sm">Error: {error}</div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {packs.map((p) => {
              const st = applyState[p.id];
              return (
                <div
                  key={p.id}
                  data-testid={`lyon-stylepack-${p.id}`}
                  className="border border-slate-700/60 rounded-lg bg-slate-900/60 p-3 hover:border-purple-500/60 transition-colors flex flex-col gap-2"
                >
                  <img
                    src={`${BACKEND_URL}${p.previewImageUrl}`}
                    alt={p.name}
                    loading="lazy"
                    data-testid={`lyon-stylepack-preview-${p.id}`}
                    onError={(e) => { e.currentTarget.style.opacity = '0.4'; }}
                    className="w-full h-24 object-cover rounded-md bg-slate-800/60 border border-slate-700/40"
                  />
                  <div>
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-bold text-white truncate">{p.name}</p>
                      <span className="text-[9px] font-mono text-slate-500">v{p.version || '1.0.0'}</span>
                    </div>
                    <p className="text-[11px] text-slate-400 mt-0.5 line-clamp-2">{p.description}</p>
                  </div>
                  {Array.isArray(p.tags) && p.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {p.tags.slice(0, 4).map((t) => (
                        <span key={t} className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-slate-800/70 text-cyan-300 border border-slate-700/60 flex items-center gap-1">
                          <Tag className="w-2 h-2" /> {t}
                        </span>
                      ))}
                    </div>
                  )}
                  <p className="text-[10px] text-slate-500">
                    {(p.recipes?.length || 0)} recetas · effects: {Object.keys(p.effects || {}).filter(k => p.effects[k] === true || (k === 'radius' || k === 'shadows')).slice(0, 3).join(', ') || '—'}
                  </p>
                  <button
                    type="button"
                    onClick={() => handleOpenDiff(p)}
                    disabled={st === 'loading' || st === 'ok'}
                    data-testid={`lyon-stylepack-apply-${p.id}`}
                    className={`mt-1 w-full px-2 py-1.5 rounded-md text-[11px] font-semibold flex items-center justify-center gap-1.5 transition-colors ${
                      st === 'ok' ? 'bg-emerald-900/40 text-emerald-200 cursor-default' :
                      st === 'err' ? 'bg-red-900/40 text-red-200' :
                      st === 'loading' ? 'bg-purple-900/40 text-purple-200 cursor-wait' :
                      'bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-500 hover:to-fuchsia-500 text-white'
                    }`}
                  >
                    {st === 'ok' ? <><CheckCircle2 className="w-3 h-3" /> {es ? 'Aplicado' : 'Applied'}</> :
                     st === 'err' ? <>{es ? 'Error' : 'Error'}</> :
                     st === 'loading' ? <><Loader2 className="w-3 h-3 animate-spin" /> {es ? 'Aplicando…' : 'Applying…'}</> :
                     <><Sparkles className="w-3 h-3" /> {es ? 'Aplicar pack' : 'Apply pack'}</>}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleOpenDiff(p)}
                    data-testid={`lyon-stylepack-open-diff-${p.id}`}
                    aria-hidden="true"
                    className="sr-only"
                  >open diff</button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {/* 🟦 V12.1 — Diff Viewer (modal sobre el panel) */}
      <LyonStylePackDiffViewer
        open={!!diffPack}
        pack={diffPack}
        currentTheme={currentTheme}
        applying={applying}
        onCancel={() => setDiffPack(null)}
        onApply={handleConfirmApply}
        es={es}
      />
    </div>
  );
};

export default LyonStylePacksPanel;
